import {
  Editor,
  getNodeType,
  getNodeAtPosition,
  isAtEndOfNode,
  isAtStartOfNode,
  isNodeActive
} from "@tiptap/core";
import { Node, NodeType } from "@tiptap/pm/model";
import { EditorState } from "@tiptap/pm/state";
import { CoreExtensionNames } from "../../constants/extention";

const findListItemPos = (typeOrName: string | NodeType, state: EditorState) => {
  const { $from } = state.selection;
  const nodeType = getNodeType(typeOrName, state.schema);

  let currentNode: Node | null = null;
  let currentDepth = $from.depth;
  let currentPos = $from.pos;
  let targetDepth: number | null = null;

  while (currentDepth > 0 && targetDepth === null) {
    currentNode = $from.node(currentDepth);

    if (currentNode.type === nodeType) targetDepth = currentDepth;
    else {
      currentDepth -= 1;
      currentPos -= 1;
    }
  }

  if (targetDepth === null) return null;

  return { $pos: state.doc.resolve(currentPos), depth: targetDepth };
};

const nextListIsDeeper = (typeOrName: string, state: EditorState) => {
  const listDepth = getNextListDepth(typeOrName, state);
  const listItemPos = findListItemPos(typeOrName, state);

  if (!listItemPos || !listDepth) return false;

  if (listDepth > listItemPos.depth) return true;

  return false;
};

const getNextListDepth = (
  typeOrName: string,
  state: EditorState
): number | null => {
  const listItemPos = findListItemPos(typeOrName, state);

  if (!listItemPos) return null;

  const [, depth] = getNodeAtPosition(
    state,
    typeOrName,
    listItemPos.$pos.pos + 4
  );

  return depth;
};

const getPrevListDepth = (
  typeOrName: string,
  state: EditorState
): number | null => {
  const listItemPos = findListItemPos(typeOrName, state);

  if (!listItemPos) return null;

  let depth = 0;
  const pos = listItemPos.$pos;

  const resolvedPos = state.doc.resolve(Math.max(pos.pos - 1, 0));

  for (let d = resolvedPos.depth; d > 0; d--) {
    const node = resolvedPos.node(d);
    if (
      [
        CoreExtensionNames.BULLET_LIST,
        CoreExtensionNames.ORDERED_LIST
      ].includes(node.type.name as CoreExtensionNames)
    )
      depth++;
  }

  depth = depth > 0 ? depth - 1 : 0;
  depth = depth * 2;

  return depth;
};

const handleBackspaceWhenNotActive = (
  editor: Editor,
  name: string,
  parentListTypes: string[]
): boolean => {
  if (!hasListBefore(editor.state, parentListTypes)) return false;

  const { $anchor } = editor.state.selection;
  const $listPos = editor.state.doc.resolve($anchor.before() - 1);
  const listDescendants: Array<{ node: Node; pos: number }> = [];

  $listPos.node().descendants((node, pos) => {
    if (node.type.name === name) listDescendants.push({ node, pos });
  });

  const lastItem = listDescendants.at(-1);
  if (!lastItem) return false;

  const $lastItemPos = editor.state.doc.resolve(
    $listPos.start() + lastItem.pos + 1
  );

  const startPos = $anchor.start() - 1;
  const endPos = $anchor.end() + 1;
  if (startPos < 0 || endPos > editor.state.doc.content.size) return false;

  return editor
    .chain()
    .cut({ from: startPos, to: endPos }, $lastItemPos.end())
    .joinForward()
    .run();
};

const handleBackspaceWhenActive = (editor: Editor, name: string): boolean => {
  if (!isAtStartOfNode(editor.state)) return false;

  const isParaSibling = isCurrentParagraphASibling(editor.state);
  const isCurrentListItemSublist = prevListIsHigher(name, editor.state);
  const listItemPos = findListItemPos(name, editor.state);
  const nextListItemIsSibling = nextListIsSibling(name, editor.state);

  if (!listItemPos) return false;

  const currentNode = listItemPos.$pos.node(listItemPos.depth);
  const currentListItemHasSubList = listItemHasSubList(
    name,
    editor.state,
    currentNode
  );

  if (currentListItemHasSubList && isCurrentListItemSublist && isParaSibling)
    return false;

  if (currentListItemHasSubList && isCurrentListItemSublist) {
    editor.chain().liftListItem(name).run();
    return editor.commands.joinItemBackward();
  }

  if (isCurrentListItemSublist && nextListItemIsSibling) return false;
  if (isCurrentListItemSublist) return false;
  if (currentListItemHasSubList) return false;

  if (hasListItemBefore(name, editor.state))
    return editor.chain().liftListItem(name).run();

  return editor.chain().liftListItem(name).run();
};

export const handleBackspace = (
  editor: Editor,
  name: string,
  parentListTypes: string[]
) => {
  if (editor.commands.undoInputRule()) return true;

  const { from, to } = editor.state.selection;
  if (from !== to) return false;

  if (!isNodeActive(editor.state, name))
    return handleBackspaceWhenNotActive(editor, name, parentListTypes);

  return handleBackspaceWhenActive(editor, name);
};

export const handleDelete = (editor: Editor, name: string) => {
  if (!isNodeActive(editor.state, name)) return false;

  if (!isAtEndOfNode(editor.state, name)) return false;

  if (nextListIsDeeper(name, editor.state))
    return editor
      .chain()
      .focus(editor.state.selection.from + 4)
      .lift(name)
      .joinBackward()
      .run();

  if (nextListIsHigher(name, editor.state))
    return editor.chain().joinForward().joinBackward().run();

  return editor.commands.joinItemForward();
};

const hasListBefore = (
  editorState: EditorState,
  parentListTypes: string[]
): boolean => {
  const { $anchor } = editorState.selection;
  const previousNodePos = Math.max(0, $anchor.pos - 2);
  const previousNode = editorState.doc.resolve(previousNodePos).node();

  return !!parentListTypes.includes(previousNode.type.name);
};

const prevListIsHigher = (typeOrName: string, state: EditorState) => {
  const listDepth = getPrevListDepth(typeOrName, state);
  const listItemPos = findListItemPos(typeOrName, state);

  if (!listItemPos || !listDepth) return false;

  if (listDepth < listItemPos.depth) return true;

  return false;
};

const nextListIsSibling = (typeOrName: string, state: EditorState) => {
  const listDepth = getNextListDepth(typeOrName, state);
  const listItemPos = findListItemPos(typeOrName, state);

  if (!listItemPos || !listDepth) return false;

  if (listDepth === listItemPos.depth) return true;

  return false;
};

const nextListIsHigher = (typeOrName: string, state: EditorState) => {
  const listDepth = getNextListDepth(typeOrName, state);
  const listItemPos = findListItemPos(typeOrName, state);

  if (!listItemPos || !listDepth) return false;

  if (listDepth < listItemPos.depth) return true;

  return false;
};

const listItemHasSubList = (
  typeOrName: string,
  state: EditorState,
  node?: Node
) => {
  if (!node) return false;

  const nodeType = getNodeType(typeOrName, state.schema);

  let hasSubList = false;

  node.descendants((child) => {
    if (child.type === nodeType) hasSubList = true;
  });

  return hasSubList;
};

const isCurrentParagraphASibling = (state: EditorState): boolean => {
  const { $from } = state.selection;
  const listItemNode = $from.node(-1);
  const currentParagraphNode = $from.parent;

  if (
    currentParagraphNode.type.name === CoreExtensionNames.PARAGRAPH &&
    [CoreExtensionNames.LIST_ITEM].includes(
      listItemNode.type.name as CoreExtensionNames
    )
  ) {
    let paragraphNodesCount = 0;
    listItemNode.forEach((child) => {
      if (child.type.name === CoreExtensionNames.PARAGRAPH)
        paragraphNodesCount++;
    });

    return paragraphNodesCount > 1;
  }

  return false;
};

const hasListItemBefore = (typeOrName: string, state: EditorState): boolean => {
  const { $anchor } = state.selection;

  const $targetPos = state.doc.resolve($anchor.pos - 2);

  if ($targetPos.index() === 0) return false;

  if ($targetPos.nodeBefore?.type.name !== typeOrName) return false;

  return true;
};
